import ServiceTemplate from '../../../templates/service/ServiceTemplate';
import type { ServiceConfig } from '../../../templates/service/types';

const config: ServiceConfig = {
  // SEO
  pageTitle: 'Electrical Installation & Upgrades Sleaford | Quantum Electrical Services',
  metaDescription: 'Expert electrical installation & upgrades in Sleaford, Lincolnshire. NAPIT registered electricians for rewires, consumer units, EV chargers, lighting & smart home. Free quotes — call 01529 265060.',
  keywords: 'electrical installation Sleaford, electrical upgrades Sleaford, consumer unit upgrade Sleaford, rewire Sleaford, EV charger installation Sleaford, LED lighting Sleaford, smart home Sleaford, electrician Sleaford',
  canonicalPath: '/services/installation-upgrades',

  // Hero
  heroBadge: 'NAPIT Registered · Sleaford & Lincolnshire',
  heroTitle: 'Electrical Installation &',
  heroHighlight: 'Upgrades',
  heroLocation: 'Sleaford, Lincolnshire',
  heroDesc: 'From full house rewires to EV charger installation — Quantum Electrical delivers premium electrical installation and upgrade services across Sleaford and Lincolnshire. NAPIT registered, Part P certified, and trusted by hundreds of local homeowners.',
  heroImageUrl: 'https://public.readdy.ai/ai/img_res/e4cfbb0225631f041974a1dc674ff580.jpg',

  // Services grid
  servicesHeading: 'Every Installation Service We Offer',
  servicesSubheading: 'Comprehensive electrical installation and upgrade services for homes and businesses across Sleaford and Lincolnshire — all NAPIT certified and Part P compliant.',
  services: [
    { icon: 'ri-flashlight-fill', title: 'Electrical Installations', desc: 'New circuits, sockets and full electrical installations to 18th Edition wiring regulations across Sleaford.' },
    { icon: 'ri-home-fill', title: 'Full & Partial Rewiring', desc: 'Complete domestic rewiring carried out with minimal disruption in Sleaford, fully certified and documented.' },
    { icon: 'ri-dashboard-fill', title: 'Consumer Unit Upgrades', desc: 'Modern RCBO consumer units for maximum circuit protection, bringing Sleaford homes up to current standards.' },
    { icon: 'ri-lightbulb-flash-fill', title: 'Lighting Installation', desc: 'Bespoke lighting solutions for any room — spotlights, pendants, strips and more throughout Sleaford.' },
    { icon: 'ri-sun-line', title: 'LED Lighting Upgrades', desc: 'Switch to energy-saving LED throughout your Sleaford home or business — big savings on every bill.' },
    { icon: 'ri-seedling-fill', title: 'Outdoor & Garden Lighting', desc: 'Landscape, pathway and feature lighting to transform outdoor spaces across Sleaford and Lincolnshire.' },
    { icon: 'ri-search-eye-fill', title: 'Security Lighting', desc: 'PIR motion sensor and floodlight installations to deter intruders and protect Sleaford properties.' },
    { icon: 'ri-plug-fill', title: 'Socket & Switch Installation', desc: 'Additional sockets, USB outlets, smart switches fitted neatly and cleanly in your Sleaford home.' },
    { icon: 'ri-car-fill', title: 'EV Charger Installation', desc: 'OZEV authorised home EV charger installations in Sleaford. Fast, reliable and grant eligible.' },
    { icon: 'ri-home-wifi-fill', title: 'Smart Home Installations', desc: 'Smart switches, automated lighting and connected home systems wired and configured in Sleaford.' },
    { icon: 'ri-robot-fill', title: 'Home Automation Systems', desc: 'Full home automation from app-controlled lighting to smart security — installed across Lincolnshire.' },
    { icon: 'ri-temp-hot-fill', title: 'Heating Controls', desc: 'Smart thermostat installation and heating control wiring for maximum comfort and efficiency in Sleaford.' },
    { icon: 'ri-water-flash-fill', title: 'Electric Shower Installation', desc: 'Zone-compliant electric shower installation with full earthing, bonding and certification in Sleaford.' },
    { icon: 'ri-restaurant-fill', title: 'Kitchen Electrical Fit-Outs', desc: 'Cooker circuits, extractor hoods, under-cabinet lighting and full kitchen electrical installations.' },
  ],

  // Why Us
  whyUs: [
    {
      icon: 'ri-shield-check-fill',
      title: 'NAPIT Registered',
      desc: 'Fully registered with NAPIT — your guarantee of competent, professional electrical installation work in Sleaford.',
    },
    {
      icon: 'ri-file-certificate-fill',
      title: 'Part P Certified',
      desc: 'Every electrical installation comes with full Part P certification, ensuring your Sleaford property meets UK regulations.',
    },
    {
      icon: 'ri-message-3-fill',
      title: 'We Actually Reply',
      desc: 'No ghosting, no chasing. Our Sleaford customers consistently praise how responsive and communicative we are.',
    },
    {
      icon: 'ri-coin-fill',
      title: 'Honest Pricing',
      desc: 'Clear, written quotes before any work begins. No hidden extras, no surprises — just fair pricing in Sleaford.',
    },
  ],

  // Process
  processSteps: [
    {
      number: '01',
      icon: 'ri-phone-fill',
      title: 'Free Quote',
      desc: 'Call or message us for a free no-obligation quote on any electrical installation in Sleaford.',
    },
    {
      number: '02',
      icon: 'ri-map-pin-2-fill',
      title: 'Site Survey',
      desc: 'We visit your Sleaford property to assess the work, plan circuits and confirm the final price.',
    },
    {
      number: '03',
      icon: 'ri-tools-fill',
      title: 'Installation',
      desc: 'Our NAPIT qualified electricians carry out the work professionally with minimum disruption.',
    },
    {
      number: '04',
      icon: 'ri-file-certificate-fill',
      title: 'Certified Sign-Off',
      desc: 'Full Part P certification and test results provided on completion — everything above board.',
    },
  ],

  // Gallery
  gallery: [
    {
      src: 'https://readdy.ai/api/search-image?query=brand%20new%20modern%20consumer%20unit%20fuse%20board%20with%20RCBO%20breakers%20installed%20on%20white%20wall%20UK%20domestic%20electrical%20installation%20premium%20neat%20professional%20wiring%20no%20people%20close%20up%20Lincolnshire%20electrician%20warm%20amber%20lighting%20high%20quality%20photography&width=800&height=600&seq=gal-inst-01-sleaford-v1&orientation=landscape',
      alt: 'New consumer unit installation Sleaford',
      caption: 'Consumer Unit Upgrade — Sleaford',
      label: 'Consumer Unit',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=wall%20mounted%20EV%20car%20charger%20electric%20vehicle%20charging%20point%20installed%20on%20red%20brick%20wall%20UK%20residential%20property%20neat%20cable%20management%20professional%20installation%20no%20people%20daylight%20exterior%20shot&width=600&height=500&seq=gal-inst-02-sleaford-v1&orientation=squarish',
      alt: 'EV charger installation Sleaford Lincolnshire',
      caption: 'Home EV Charger — Sleaford',
      label: 'EV Charger',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20recessed%20LED%20spotlights%20ceiling%20installation%20UK%20home%20interior%20bright%20contemporary%20kitchen%20no%20people%20warm%20white%20lighting%20premium%20finish%20professional%20electrical%20lighting%20installation&width=600&height=500&seq=gal-inst-03-sleaford-v1&orientation=squarish',
      alt: 'LED lighting installation Sleaford',
      caption: 'LED Lighting Installation — Kitchen',
      label: 'LED Lighting',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=professional%20smart%20home%20control%20panel%20touchscreen%20wall%20mounted%20UK%20modern%20interior%20home%20automation%20system%20installation%20no%20people%20contemporary%20home%20technology%20sleek%20design%20Lincolnshire&width=600&height=500&seq=gal-inst-04-sleaford-v1&orientation=squarish',
      alt: 'Smart home installation Sleaford',
      caption: 'Smart Home System — Sleaford',
      label: 'Smart Home',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=outdoor%20garden%20pathway%20LED%20lighting%20UK%20garden%20night%20photography%20warm%20amber%20glow%20lights%20along%20stone%20path%20lush%20green%20garden%20Lincolnshire%20no%20people%20dramatic%20moody%20exterior%20landscape%20lighting&width=600&height=500&seq=gal-inst-05-sleaford-v1&orientation=squarish',
      alt: 'Outdoor garden lighting Sleaford',
      caption: 'Outdoor Garden Lighting — Sleaford',
      label: 'Garden Lighting',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=new%20electrical%20wiring%20installation%20UK%20domestic%20property%20neat%20cable%20routing%20wall%20chasing%20plasterboard%20professional%20electrician%20work%20no%20people%20clean%20tidy%20cable%20management%20domestic%20rewire%20Lincolnshire&width=600&height=500&seq=gal-inst-06-sleaford-v1&orientation=squarish',
      alt: 'Full rewire Sleaford Lincolnshire',
      caption: 'Domestic Rewire — Lincolnshire',
      label: 'Full Rewire',
    },
  ],

  // FAQs
  faqHeading: 'Installation & Upgrades Questions',
  faqSubheading: 'Common questions about our electrical installation and upgrade services in Sleaford and across Lincolnshire.',
  faqs: [
    {
      q: 'What electrical installations do you carry out in Sleaford?',
      a: 'We carry out a full range of electrical installations across Sleaford and Lincolnshire — from full house rewires and consumer unit upgrades to EV charger installation, LED lighting, smart home systems, outdoor lighting, kitchen electrics and more. All work is carried out to 18th Edition IET Wiring Regulations and certified under Part P.',
    },
    {
      q: 'How long does a full house rewire take in Sleaford?',
      a: 'A full house rewire in Sleaford typically takes 3–5 days for an average 3-bedroom property, depending on access and the condition of the existing installation. We work efficiently to minimise disruption, protect your furnishings, and leave the property clean and tidy. All rewires are certified under Part P on completion.',
    },
    {
      q: 'Do I need a consumer unit upgrade at my Sleaford home?',
      a: 'If your consumer unit is more than 10–15 years old, contains rewirable fuses rather than circuit breakers, or lacks RCD protection, an upgrade is strongly recommended. Modern consumer units with RCBO protection provide far better circuit-level protection. We carry out consumer unit upgrades throughout Sleaford and Lincolnshire with full Part P certification.',
    },
    {
      q: 'Can you install an EV charger at my home in Sleaford?',
      a: 'Yes — we are OZEV authorised installers, meaning we can carry out the full EV charger installation process at your Sleaford property, including the initial survey, installation of a dedicated charge point, and any applicable grant paperwork. We install home EV chargers for all major vehicle brands.',
    },
    {
      q: 'What smart home installations do you offer in Sleaford?',
      a: 'We install a wide range of smart home systems across Sleaford including smart lighting controls, automated blinds, smart thermostats, app-controlled sockets and switches, and full home automation systems. All smart home wiring and configuration is carried out by our qualified electricians.',
    },
    {
      q: 'Do you install outdoor and security lighting in Sleaford?',
      a: 'Yes — we install all types of outdoor electrical lighting in Sleaford including PIR security floodlights, motion sensor lighting, garden and pathway lights, feature lighting and driveway illumination. All outdoor electrical installations are carried out to current BS7671 regulations with full weatherproofing.',
    },
    {
      q: 'Are your electrical installations Part P certified in Sleaford?',
      a: 'Absolutely. Every electrical installation we carry out in Sleaford and across Lincolnshire is fully notified and certified under Part P of the Building Regulations. We provide you with a Building Regulations Compliance Certificate, an Electrical Installation Certificate, and test results on completion — essential for insurance and property sales.',
    },
    {
      q: 'How much does it cost to upgrade the electrics in a Sleaford property?',
      a: 'Costs vary depending on the size of the property and the scope of work. A consumer unit upgrade in Sleaford typically starts from around £400–£600. A full rewire of a 3-bed property varies based on condition and access. We always provide a free, detailed written quote before any work begins — call us on 01529 265060 for an accurate price.',
    },
    {
      q: 'Can you add more sockets or new circuits to my Sleaford home?',
      a: 'Yes — adding additional sockets, USB outlets, fused spurs or entirely new circuits is one of the most common jobs we carry out in Sleaford. Whether you need extra sockets in a kitchen extension, a dedicated circuit for a new appliance, or USB outlets throughout the home, we\'ll carry out the work neatly and certify it properly.',
    },
    {
      q: 'Do you install electric showers in Sleaford?',
      a: 'Yes — electric shower installation is a specialist task requiring correct circuit sizing, earthing, and supplementary bonding to comply with BS7671 bathroom zone requirements. Our NAPIT registered electricians carry out electric shower installations throughout Sleaford and Lincolnshire with full certification on completion.',
    },
  ],

  // Form
  formUrl: 'https://readdy.ai/api/form/d73p7cb41deorl02eg80',
};

export default function InstallationUpgradesPage() {
  return <ServiceTemplate config={config} />;
}
